/**
 * Environmental Geotech — Carbon Credit Verification  (Step 8b of the 8-step intelligence loop)
 *
 * Monitor biomass integrity and verify carbon credit parcels using multispectral
 * drone survey data. Tracks canopy cover percentage, above-ground biomass (AGB) estimates,
 * and flags parcels that may have had canopy loss (invalidating carbon credits).
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Leaf, RefreshCw, TrendingDown, TrendingUp, BarChart2,
  ChevronDown, ChevronUp, CheckCircle2, AlertTriangle,
} from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import Alert from '../../components/common/Alert';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface CarbonMission {
  id: string;
  name: string;
  status: string;
  created_at: string;
  updated_at: string;
  metadata?: {
    ai_analysis_status?: string;
    vertical?: string;
    zone_name?: string;
    step7_status?: string;
    diagnosis?: string;
    canopy_cover_pct?: number;
    canopy_loss_ha?: number;
    canopy_loss_pct?: number;
    agb_tonnes_ha?: number;        // above-ground biomass tonnes / ha
    co2_sequestration_t?: number;  // CO₂ equivalent sequestration tonnes
    credit_status?: string;
    [key: string]: any;
  };
}

// ─── Canopy cover status helpers ─────────────────────────────────────────────

function CoverBar({ pct }: { pct: number | undefined | null }) {
  if (pct == null) return null;
  const clamped = Math.min(100, Math.max(0, pct));
  const color = clamped >= 80 ? 'bg-green-500' : clamped >= 60 ? 'bg-amber-400' : clamped >= 40 ? 'bg-orange-500' : 'bg-red-500';
  return (
    <div className="mt-2">
      <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
        <span>Canopy Cover</span><span>{clamped.toFixed(1)}%</span>
      </div>
      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
        <div className={`${color} h-2 rounded-full transition-all`} style={{ width: `${clamped}%` }} />
      </div>
    </div>
  );
}

function StatCell({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="text-center">
      <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
      <p className="text-lg font-bold text-gray-900 dark:text-white mt-0.5">{value ?? '—'}</p>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export const CarbonCreditVerificationPage: React.FC = () => {
  const navigate = useNavigate();
  const [missions, setMissions]     = useState<CarbonMission[]>([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/missions?mission_type=environmental_survey&limit=100');
      const all: CarbonMission[] = res.data?.missions ?? [];
      setMissions(
        all.filter(
          (m) =>
            m.metadata?.vertical === 'environmental' &&
            m.metadata?.ai_analysis_status === 'complete'
        )
      );
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to load carbon data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Portfolio summary
  const totalCO2  = missions.reduce((s, m) => s + (m.metadata?.co2_sequestration_t ?? 0), 0);
  const avgCover  = missions.length
    ? missions.reduce((s, m) => s + (m.metadata?.canopy_cover_pct ?? 0), 0) / missions.length
    : 0;
  const atRisk    = missions.filter((m) => (m.metadata?.canopy_loss_pct ?? 0) > 5).length;

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Leaf className="w-7 h-7 text-green-600" /> Carbon Credit Verification
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Step 8b — Monitor biomass integrity and verify carbon credit parcels from canopy drone surveys
          </p>
        </div>
        <Button variant="outline" onClick={load}><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
      </div>

      {error && <Alert type="error" onClose={() => setError(null)}>{error}</Alert>}

      {/* ── Portfolio summary ── */}
      {missions.length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          <Card padding="sm">
            <p className="text-xs text-gray-500 dark:text-gray-400">Est. CO₂ Sequestration</p>
            <p className="text-2xl font-bold mt-1 text-green-700 dark:text-green-400">
              {totalCO2 > 0 ? `${(totalCO2 / 1000).toFixed(1)}k` : '0'} t
            </p>
          </Card>
          <Card padding="sm">
            <p className="text-xs text-gray-500 dark:text-gray-400">Avg Canopy Cover</p>
            <p className={`text-2xl font-bold mt-1 ${avgCover >= 70 ? 'text-green-700 dark:text-green-400' : 'text-amber-700 dark:text-amber-400'}`}>
              {avgCover.toFixed(1)}%
            </p>
          </Card>
          <Card padding="sm">
            <p className="text-xs text-gray-500 dark:text-gray-400">Parcels at Risk (&gt;5% loss)</p>
            <p className={`text-2xl font-bold mt-1 ${atRisk > 0 ? 'text-red-700 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'}`}>
              {atRisk}
            </p>
          </Card>
        </div>
      )}

      {/* ── Parcel list ── */}
      <Card padding="none">
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700">
          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            {missions.length} parcel{missions.length !== 1 ? 's' : ''} with biomass data
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-14"><Spinner size="lg" /></div>
        ) : missions.length === 0 ? (
          <div className="text-center py-14">
            <BarChart2 className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No biomass data available</p>
            <p className="text-sm text-gray-400 mt-1">Complete environmental drone surveys and AI analysis first</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {missions.map((m) => {
              const isExpanded    = expandedId === m.id;
              const lossP         = m.metadata?.canopy_loss_pct;
              const isAtRisk      = (lossP ?? 0) > 5;

              return (
                <div key={m.id}>
                  <button
                    className="w-full flex items-center gap-4 px-5 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/30 text-left"
                    onClick={() => setExpandedId(isExpanded ? null : m.id)}
                  >
                    {isAtRisk
                      ? <TrendingDown className="w-4 h-4 flex-shrink-0 text-red-500" />
                      : <TrendingUp className="w-4 h-4 flex-shrink-0 text-green-500" />
                    }
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{m.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {m.metadata?.zone_name ?? '—'} · {new Date(m.updated_at).toLocaleDateString('en-GB')}
                      </p>
                    </div>
                    {isAtRisk ? (
                      <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
                        <AlertTriangle className="w-3 h-3" /> At Risk
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                        <CheckCircle2 className="w-3 h-3" /> Verified
                      </span>
                    )}
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                  </button>

                  {isExpanded && (
                    <div className="px-5 pb-5 pt-2 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-700">
                      <div className="grid grid-cols-3 gap-6 mb-4">
                        <StatCell label="AGB (t/ha)"       value={m.metadata?.agb_tonnes_ha != null ? Number(m.metadata.agb_tonnes_ha).toFixed(1) : null} />
                        <StatCell label="CO₂ Seq. (t)"     value={m.metadata?.co2_sequestration_t != null ? Number(m.metadata.co2_sequestration_t).toLocaleString() : null} />
                        <StatCell label="Canopy Loss (ha)"  value={m.metadata?.canopy_loss_ha != null ? <span className="text-red-600 dark:text-red-400">{Number(m.metadata.canopy_loss_ha).toFixed(1)}</span> : null} />
                      </div>
                      <CoverBar pct={m.metadata?.canopy_cover_pct} />
                      {m.metadata?.diagnosis && (
                        <p className="mt-3 text-sm text-gray-600 dark:text-gray-400 italic">{m.metadata.diagnosis}</p>
                      )}
                      <div className="mt-4">
                        <Button size="sm" variant="outline" onClick={() => navigate(`/missions/${m.id}`)}>
                          Open Mission
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
};

export default CarbonCreditVerificationPage;

// Made with Bob
